import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomPeriodSelect } from '@/features/global-filter/components/sections-content/PeriodContent/components/CustomPeriodSelect/CustomPeriodSelect';
import { DatePicker } from './components/DatePicker/DatePicker';
import { CustomTooltip } from '@/core/components/CustomTooltip/CustomTooltip';
import { formatRangeString, getCalculatedDates, clampToMaxRangeDays, RESTRICTED_MAX_RANGE_DAYS } from '@/core/utils/dateUtils';
import { dispatchFilterEvent } from '../../GlobalFilterModal';
import './PeriodContent.scss';

const BdsIcon = 'bds-icon' as any;

export interface PeriodOption {
  label: string;
  value: string;
}

export interface PeriodData {
  dataType: string;
  dataRange: string;
  formattedDateRange: string;
  startDate?: Date;
  endDate?: Date;
}

export interface PeriodContentProps {
  values?: PeriodData;
  onChange?: (values: PeriodData) => void;
  limit3Months?: boolean;
  lockedToLast24h?: boolean;
}

export const PeriodContent: React.FC<PeriodContentProps> = ({
  values,
  onChange,
  limit3Months = false,
  lockedToLast24h = false,
}) => {
  const { t } = useTranslation();
  const isInitialized = useRef(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const [activeControl, setActiveControl] = useState<string | null>(null);

  const dataTypeOptions: PeriodOption[] = useMemo(
    () => [
      { label: t('globalFilter.publicationDate'), value: 'publication_date' },
      { label: t('globalFilter.classificationDate'), value: 'classification_date' },
    ],
    [t]
  );

  const periodRangeOptions: PeriodOption[] = useMemo(() => {
    const allOptions: PeriodOption[] = [
      { label: t('globalFilter.today'), value: 'today' },
      { label: t('globalFilter.yesterday'), value: 'yesterday' },
      { label: t('globalFilter.last24hours'), value: 'last24h' },
      { label: t('globalFilter.currentWeek'), value: 'current_week' },
      { label: t('globalFilter.currentMonth'), value: 'current_month' },
      { label: t('globalFilter.lastMonth'), value: 'last_month' },
      { label: t('globalFilter.last7days'), value: 'last7d' },
      { label: t('globalFilter.last30days'), value: 'last30d' },
      { label: t('globalFilter.last3months'), value: 'last3m' },
      { label: t('globalFilter.last360days'), value: 'last360d' },
      { label: t('globalFilter.wholePeriod'), value: 'whole_period' },
      { label: t('globalFilter.custom'), value: 'custom' },
    ];
    const restrictedOptionValues = new Set(['last360d', 'whole_period']);
    return limit3Months ? allOptions.filter((o) => !restrictedOptionValues.has(o.value)) : allOptions;
  }, [t, limit3Months]);

  const initialRange = values?.dataRange || (limit3Months ? 'last30d' : 'last7d');
  const initialDataType = values?.dataType || dataTypeOptions[0].value;
  const initialDates = useMemo(() => getCalculatedDates(initialRange), [initialRange]);

  const [selectedDataType, setSelectedDataType] = useState(initialDataType);
  const [selectedPeriodRange, setSelectedPeriodRange] = useState(initialRange);
  const [startDate, setStartDate] = useState<Date | undefined>(values?.startDate || initialDates?.start);
  const [endDate, setEndDate] = useState<Date | undefined>(values?.endDate || initialDates?.end);
  const lastEmittedRef = useRef<PeriodData | undefined>(values);

  useLayoutEffect(() => {
    if (!values) return;
    
    let start = values.startDate;
    let end = values.endDate;
    let range = values.dataRange;

    if (limit3Months && start && end) {
      const spanMs = end.getTime() - start.getTime();
      if (spanMs > RESTRICTED_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000) {
        const clamped = clampToMaxRangeDays(start, end, RESTRICTED_MAX_RANGE_DAYS);
        start = clamped.start;
        end = clamped.end;
        range = 'custom';
      }
    }

    setSelectedDataType(values.dataType);
    setSelectedPeriodRange(range);
    setStartDate(start);
    setEndDate(end);
  }, [values, limit3Months]);

  const notifyChange = useCallback((type: string, range: string, start?: Date, end?: Date) => {
    if (!onChangeRef.current) return;
    const next: PeriodData = {
      dataType: type,
      dataRange: range,
      formattedDateRange: formatRangeString(range, start, end),
      startDate: start,
      endDate: end,
    };
    lastEmittedRef.current = next;
    onChangeRef.current(next);
  }, []);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    if (!values?.startDate || !values?.endDate) {
      if (initialDates && onChangeRef.current) {
        onChangeRef.current({
          dataType: initialDataType,
          dataRange: initialRange,
          formattedDateRange: formatRangeString(initialRange, initialDates.start, initialDates.end),
          startDate: initialDates.start,
          endDate: initialDates.end,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDataTypeChange = (value: string) => {
    setSelectedDataType(value);
    notifyChange(value, selectedPeriodRange, startDate, endDate);
    dispatchFilterEvent('PERIOD', 'DATA_TYPE', value);
  };

  const handlePeriodRangeChange = (value: string) => {
    setSelectedPeriodRange(value);
    const dates = getCalculatedDates(value);

    if (!dates) {
      notifyChange(selectedDataType, value, startDate, endDate);
      dispatchFilterEvent('PERIOD', 'RANGE', value);
      return;
    }

    let finalStart = dates.start;
    let finalEnd = dates.end;

    if (limit3Months) {
      const spanMs = finalEnd.getTime() - finalStart.getTime();
      if (spanMs > RESTRICTED_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000) {
        const clamped = clampToMaxRangeDays(finalStart, finalEnd, RESTRICTED_MAX_RANGE_DAYS);
        finalStart = clamped.start;
        finalEnd = clamped.end;
      }
    }

    setStartDate(finalStart);
    setEndDate(finalEnd);
    notifyChange(selectedDataType, value, finalStart, finalEnd);
    dispatchFilterEvent('PERIOD', 'RANGE', value);
  };

  const handleDateRangeChange = (newStartDate: Date, newEndDate: Date) => {
    let finalStart = newStartDate;
    let finalEnd = newEndDate;

    if (limit3Months) {
      const spanMs = finalEnd.getTime() - finalStart.getTime();
      if (spanMs > RESTRICTED_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000) {
        const clamped = clampToMaxRangeDays(finalStart, finalEnd, RESTRICTED_MAX_RANGE_DAYS);
        finalStart = clamped.start;
        finalEnd = clamped.end;
      }
    }

    setStartDate(finalStart);
    setEndDate(finalEnd);
    setSelectedPeriodRange('custom');
    notifyChange(selectedDataType, 'custom', finalStart, finalEnd);
    dispatchFilterEvent('PERIOD', 'CUSTOM_DATE', { start: finalStart, end: finalEnd });
  };

  return (
    <div className="period-content">
      <div className="period-content__section">
        <div className="period-content__header">
          <span className="period-content__label">{t('globalFilter.dataType')}</span>
          <CustomTooltip content={t('globalFilter.dataTypeTooltip')} position="top" maxWidth="182px">
            <BdsIcon name="info" size="x-small" />
          </CustomTooltip>
        </div>
        <div className="period-content__input">
          <CustomPeriodSelect
            options={dataTypeOptions}
            value={selectedDataType}
            onChange={handleDataTypeChange}
            className="period-select--compact"
            isOpen={activeControl === 'dataType'}
            disabled={lockedToLast24h}
            onOpenChange={(open) => setActiveControl(open ? 'dataType' : null)}
          />
        </div>
      </div>

      <div className="period-content__section">
        <div className="period-content__header">
          <span className="period-content__label">{t('globalFilter.period')}</span>
        </div>
        <div className="period-content__input">
          <CustomPeriodSelect
            options={periodRangeOptions}
            value={selectedPeriodRange}
            onChange={handlePeriodRangeChange}
            isOpen={activeControl === 'periodRange'}
            disabled={lockedToLast24h}
            onOpenChange={(open) => setActiveControl(open ? 'periodRange' : null)}
          />
        </div>
      </div>

      <div className="period-content__section">
        {lockedToLast24h ? (
          <span className="period-content__label">{t('globalFilter.periodFixed24h')}</span>
        ) : (
          <DatePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            maxRangeDays={limit3Months ? 90 : undefined}
            activeControl={activeControl}
            onActiveControlChange={setActiveControl}
          />
        )}
      </div>
    </div>
  );
};